// src/pages/PostcodeQuoteMinibus.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import PostcodeInput from "../components/PostcodeInput";
import BookingForm from "../components/BookingForm";
import { fareClientRules } from "../config/fareClientRules";
import api from "../utils/api";

const PostcodeQuoteMinibus = () => {
  const [fromPostcode, setFromPostcode] = useState("");
  const [toPostcode, setToPostcode] = useState("");
  const [distance, setDistance] = useState(null);
  const [fare, setFare] = useState(null);
  const [vehicleLabel, setVehicleLabel] = useState("Minibus");
  const [vehicleType, setVehicleType] = useState("minibus");
  const [showForm, setShowForm] = useState(false);
  const [errors, setErrors] = useState({});
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [quoteId, setQuoteId] = useState(null);

  // ✅ Default passengers for Minibus (7–14 range)
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    datetime: "",
    vehicle: "minibus",
    passengers: 7,
    bags: "",
    notes: "",
    flight_number: "",
    pickup_time: "",
  });

  const maxPassengers = 14;
  const minPassengers = 7;
  const maxBags = 10;

  const [pickupTime, setPickupTime] = useState(
    new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16)
  );

  /* ---------------------------------------------------------------------- */
  /* 💷 Fetch fare quote                                                    */
  /* ---------------------------------------------------------------------- */
  const getDistance = async () => {
    if (!fromPostcode.trim() || !toPostcode.trim()) {
      setErrors({ quote: "Please enter both pickup and drop-off locations." });
      return;
    }

    if (formData.passengers < minPassengers || formData.passengers > maxPassengers) {
      setErrors({ quote: `For minibus hire, please select between ${minPassengers} and ${maxPassengers} passengers.` });
      return;
    }

    if (formData.bags === "" || isNaN(Number(formData.bags))) {
      setErrors({ quote: "Please enter number of bags (use 0 if none)." });
      return;
    }

    setLoadingQuote(true);
    setErrors({});

    try {
      const res = await api.post("/api/get-quote", {
        pickup: fromPostcode,
        dropoff: toPostcode,
        passengers: parseInt(formData.passengers, 10),
        bags: parseInt(formData.bags, 10),
        pickup_time: pickupTime,
      });

      if (res.data && res.data.fare) {
        setDistance(res.data.distance);
        setFare(res.data.fare);
        setVehicleLabel(res.data.vehicleLabel || "Minibus");
        setVehicleType(res.data.vehicleType || "minibus");
        setQuoteId(res.data.quote_id || null);
        setShowForm(true);
        return;
      }

      if (res.data && res.data.comingSoon) {
        setErrors({
          quote:
            "🚧 Sorry, we’re not yet operating full coverage for this route.<br/>" +
            "We’re expanding rapidly — please check back soon or try another nearby pickup area. 🚐",
        });
        return;
      }

      if (res.data && res.data.error) {
        setErrors({ quote: `⚠️ ${res.data.error}` });
        return;
      }

      setErrors({
        quote:
          "We’re temporarily unable to calculate a fare for this route.<br/>" +
          "Please verify your pickup and drop-off details or try again shortly.",
      });
    } catch (err) {
      console.error("❌ Error fetching fare:", err);
      setErrors({
        quote:
          err.response?.data?.error ||
          "⚠️ Our system is temporarily busy. Please try again in a few moments.",
      });
    } finally {
      setLoadingQuote(false);
    }
  };

  /* ---------------------------------------------------------------------- */
  /* 🧾 Booking Form Handlers                                               */
  /* ---------------------------------------------------------------------- */
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prevData) => ({
      ...prevData,
      [name]: value,
    }));
  };

  /* ---------------------------------------------------------------------- */
  /* 📩 Submit booking                                                     */
  /* ---------------------------------------------------------------------- */
  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      const response = await api.post("/api/bookings", {
        ...formData,
        pickup_location: fromPostcode,
        dropoff_location: toPostcode,
        pickup_time: pickupTime,
        fare: fare,
        quote_id: quoteId || null,
      });

      if ((response.status === 200 || response.status === 201) && response.data.ref_code) {
        localStorage.removeItem("lastQuote");
        window.location.assign(response.data.paymentLink);
      } else {
        alert("Booking saved but unable to redirect. Please contact support.");
      }
    } catch (error) {
      console.error("Error submitting booking:", error);
      setErrors({ form: "Error submitting booking. Please try again." });
    }
  };

  /* ---------------------------------------------------------------------- */
  /* 🖼️ UI                                                                */
  /* ---------------------------------------------------------------------- */
  return (
    <div
      id="taxi-quote-form"
      style={{
        maxWidth: "750px",
        width: "95%",
        margin: "2rem auto",
        padding: "2.5rem",
        backgroundColor: "#fff",
        borderRadius: "12px",
        boxShadow: "0 6px 18px rgba(0,0,0,0.15)",
      }}
    >
      <h2
        style={{
          textAlign: "center",
          marginBottom: "1.8rem",
          color: "#222",
          fontSize: "26px",
          fontWeight: "700",
        }}
      >
        🚐 Get Your Minibus Fare
      </h2>

      <p style={{ textAlign: "center", marginBottom: "1.5rem", color: "#555" }}>
        Perfect for 7–14 passengers – ideal for airport transfers, events, and group trips.
      </p>

      {/* Pickup Input */}
      <PostcodeInput
        placeholder="Pickup Location"
        value={fromPostcode}
        onChange={setFromPostcode}
        onPlaceSelected={(place) => setFromPostcode(place.formatted_address)}
        restrictions={{ country: "uk" }}
      />

      {/* Drop-off Input */}
      <PostcodeInput
        placeholder="Drop-off Location"
        value={toPostcode}
        onChange={setToPostcode}
        onPlaceSelected={(place) => setToPostcode(place.formatted_address)}
        restrictions={{ country: "uk" }}
      />

      {/* Passengers */}
      <label>Passengers (7–14)</label>
      <input
        type="number"
        name="passengers"
        min={minPassengers}
        max={maxPassengers}
        value={formData.passengers}
        onChange={(e) => {
          const val = Math.max(minPassengers, Math.min(Number(e.target.value), maxPassengers));
          setFormData((prev) => ({ ...prev, passengers: val }));
        }}
        style={{ width: "100%", padding: "12px", marginBottom: "12px" }}
      />

      {/* Vehicle selection (pre-limited to 9 or 14 seater options) */}

      {/* Bags */}
      <label>Bags</label>
      <input
        type="number"
        name="bags"
        placeholder={`Bags (max ${maxBags})`}
        min="0"
        max={maxBags}
        value={formData.bags}
        onChange={(e) => {
          const val = Math.min(Number(e.target.value), maxBags);
          setFormData((prev) => ({ ...prev, bags: val }));
        }}
        style={{ width: "100%", padding: "12px", marginBottom: "12px" }}
      />

      {/* Pickup Time */}
      <label>Pickup Time</label>
      <input
        type="datetime-local"
        value={pickupTime}
        onChange={(e) => setPickupTime(e.target.value)}
        min={new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString().slice(0, 16)}
      />

      {/* Errors */}
      {errors.quote && (
        <div
          className="error-banner"
          style={{ marginTop: "10px", color: "#b30000", textAlign: "center" }}
          dangerouslySetInnerHTML={{ __html: errors.quote }}
        />
      )}

      {/* Quote Button */}
      <button
        style={{
          width: "100%",
          padding: "16px",
          backgroundColor: "#ffd24d",
          color: "#000",
          fontWeight: "700",
          fontSize: "18px",
          border: "none",
          borderRadius: "6px",
          marginTop: "14px",
          cursor: "pointer",
          transition: "background 0.3s",
        }}
        onClick={getDistance}
        onMouseOver={(e) => (e.target.style.backgroundColor = "#ffb300")}
        onMouseOut={(e) => (e.target.style.backgroundColor = "#ffd24d")}
      >
        {loadingQuote ? "Calculating..." : "Get Minibus Quote"}
      </button>

      {/* Quote Summary */}
      {fare && !showForm && (
        <p
          style={{
            textAlign: "center",
            marginTop: "15px",
            fontWeight: "bold",
            color: "#444",
            fontSize: "18px",
          }}
        >
          Distance: <b>{distance} km</b> | Fare: <b>£{fare}</b>
          {vehicleLabel && (
            <>
              <br />
              Vehicle: <b>{vehicleLabel}</b>
            </>
          )}
        </p>
      )}

      {/* Booking Form */}
      {showForm && (
        <BookingForm
          formData={formData}
          errors={errors}
          handleInputChange={handleInputChange}
          handleSubmit={handleSubmit}
          fare={fare}
          pickupLocation={fromPostcode}
          dropoffLocation={toPostcode}
          suggestedVehicle={vehicleLabel}
        />
      )}
    </div>
  );
};

export default PostcodeQuoteMinibus;

